import React, { useMemo, useRef } from 'react';
import {
    Box,
    Tab,
} from "@mui/material";
import {
    InventoryAdvancedSearchParams,
    InventoryBasicSearchParams,
    SearchTab,
} from "../../model/Inventory";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import BasicSearchForm, { BasicSearchFormParams, BasicSearchFormProps } from '../../components/forms/BasicSearchForm';
import AdvancedSearchForm, {
    AdvancedSearchFormParams,
    AdvancedSearchFormParamType,
    AdvancedSearchFormProps
} from '../../components/forms/AdvancedSearchForm';
import GDGAppBar from "../../components/app/GDGAppBar/GDGAppBar";
import GDGAppBarButton from "../../components/app/GDGAppBar/GDGAppBarButton";
import {
    setAdvancedSearchParams,
    setBasicSearchParams, setFileSearchParams, setInventoryData, setInventoryDataRetrievedTS,
    setSearchType, setBasicSearchParamsCsv,
} from "../../features/inventory/inventorySlice";
import { useAppDispatch, useAppSelector } from "../../util/hooks";
import { RootState } from "../../util/store";
import { useHistory } from "react-router-dom";
import {
    useGetItemsAdvancedSearchQuery,
    useGetItemsBasicSearchQuery,
    useGetUserInfoQuery,
    useGetItemsFileSearchMutation
} from "../../services/grace";
import { FormikBag, withFormik} from "formik";
import * as Yup from "yup";
import { DateRange} from "@mui/x-date-pickers-pro";
import { formatDateToISO, parseDate} from "../../util/date";
import { isEqual } from "lodash";
import FileSearchForm, {
    FileSearchDelimiterType,
    FileSearchFieldType,
    FileSearchFormParams,
    FileSearchFormProps,
    FileSearchItemType
} from "../../components/forms/FileSearchForm";
import { AdvancedSearch } from "../../model/User";
import { format } from "date-fns";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useAlertDispatch } from "../../context/AlertContext/AlertContext";
//redux
import { setOptionsBasic, setSearchBasicFormText, setSelectedValuesBasicForm } from '../../features/search/searchSlice';

export default function Search(): JSX.Element {
    const history = useHistory();
    const dispatch = useAppDispatch();
    const alertDispatch = useAlertDispatch();
    const searchType = useAppSelector((state: RootState) => state.inventory.searchType);
    const basicSearch = useAppSelector((state: RootState) => state.inventory.basicSearch);
    const advancedSearch = useAppSelector((state: RootState) => state.inventory.advancedSearch);

    const {
        refetch: basicRefetch,
    } = useGetItemsBasicSearchQuery(basicSearch.params, { skip: !basicSearch.active });

    const {
        refetch: advancedRefetch,
    } = useGetItemsAdvancedSearchQuery(advancedSearch.params, { skip: !advancedSearch.active });

    const [getItemsFileSearch] = useGetItemsFileSearchMutation();

    const { data: userData } = useGetUserInfoQuery();

    const handleClose = () => {
        // Reset the form
        dispatch(setOptionsBasic(null));
        dispatch(setSearchBasicFormText(null));
        dispatch(setSelectedValuesBasicForm(null));
        
        history.replace("/inventory");
    };

    const convertBasicSearchParams = (values: BasicSearchFormParams, iscsv = false):InventoryBasicSearchParams => {
        const { fullRecord, fullResults, addDate, destroyDate, statusDate, contentsDate, ...rest } = values;
        return {
            ...rest,
            fullRecord: (iscsv) ? true : fullRecord,
            fullResults: (iscsv) ? true : fullResults,
            addDate: {
                from: formatDateToISO(addDate[0]),
                to: formatDateToISO(addDate[1]),
            },
            destroyDate: {
                from: formatDateToISO(destroyDate[0]),
                to: formatDateToISO(destroyDate[1]),
            },
            statusDate: {
                from: formatDateToISO(statusDate[0]),
                to: formatDateToISO(statusDate[1]),
            },
            contentsDate: {
                from: formatDateToISO(contentsDate[0]),
                to: formatDateToISO(contentsDate[1]),
            },
        }
    }

    const handleBasicSubmit = (values: BasicSearchFormParams, formikBag: FormikBag<BasicSearchFormProps, BasicSearchFormParams>) => {
        const basicSearchValues = convertBasicSearchParams(values);
        const basicSearchValuesCsv = convertBasicSearchParams(values, true);
        if (isEqual(basicSearchValues, basicSearch.params)) {
            // Catch the first time the "search" button is clicked
            // after loading (when the search parameters are already
            // set but nothing has been retrieved yet)
            if (!basicSearch.active) {
                dispatch(setBasicSearchParams(basicSearchValues));
                dispatch(setBasicSearchParamsCsv(basicSearchValuesCsv));
            } else {
                // Otherwise, we assume that if the user clicks on the "search" button
                // again in the dialog then they want to run the same query again.
                basicRefetch();
            }
        } else {
            dispatch(setBasicSearchParams(basicSearchValues));
            dispatch(setBasicSearchParamsCsv(basicSearchValuesCsv));
        }
        history.replace("/inventory");
    };

    const handleAdvancedSubmit = (values: AdvancedSearchFormParams, formikBag: FormikBag<AdvancedSearchFormProps, AdvancedSearchFormParams>) => {
        const advancedSearchValues:InventoryAdvancedSearchParams = {
            code: values.selectedOption !== undefined ? values.selectedOption.code : "",
            parameters: [],
        };

        Object.keys(values.parameters).forEach((key) => {
            advancedSearchValues.parameters.push({code: key, value: values.parameters[key]});
        });

        if (advancedSearchValues === advancedSearch.params) {
            // If the user clicks on the "search" button
            // again in the dialog then we assume they
            // want to run the query again.
            advancedRefetch();
        } else {
            dispatch(setAdvancedSearchParams(advancedSearchValues));
        }
        history.replace("/inventory");
    };

    const handleSearchTabChange = (tab: SearchTab) => {
        dispatch(setSearchType(tab));
    };

    const dateRangeTest = (value:Date[] | undefined): string | null => {
        if ((value === undefined) || (value[0] === null && value[1] === null)) {
            return null; // It's ok for both to be empty
        }

        if ((value[0] !== null && value[1] === null) || (value[0] === null && value[1] !== null)) {
            return "you must enter a start and end date, not one or the other";
        }

        if (value[0] > value[1]) {
                return "start date must be less than or equal to end date";
        }

        return null;
    }

    const basicSearchValidationSchema = Yup.object().shape({
        addDate: Yup.array()
            .test({
                name: "valid-addDate",
                test: async (value, { createError }) => {
                    const result = dateRangeTest(value);
                    if (result !== null) {
                        return createError({message: result});
                    }

                    return true;
                }
            }),
        destroyDate: Yup.array()
            .test({
                name: "valid-destroyDate",
                test: async (value, { createError }) => {
                    const result = dateRangeTest(value);
                    if (result !== null) {
                        return createError({message: result});
                    }

                    return true;
                }
            }),
        statusDate: Yup.array()
            .test({
                name: "valid-statusDate",
                test: async (value, { createError }) => {
                    const result = dateRangeTest(value);
                    if (result !== null) {
                        return createError({message: result});
                    }

                    return true;
                }
            }),
        contentsDate: Yup.array()
            .test({
                name: "valid-addDate",
                test: async (value, { createError }) => {
                    const result = dateRangeTest(value);
                    if (result !== null) {
                        return createError({message: result});
                    }

                    return true;
                }
            }),
    });

    // const advancedSearchValidationSchema = Yup.object().shape({
    //     selectedOption: Yup.object()
    //         .test({
    //             name: "valid-form",
    //             test: (value:AdvancedSearch | null, {parent, createError}) => {
    //                 if (value.parameters.length === 0) {
    //                     return true;
    //                 }
    //
    //                 console.log("VALIDATE");
    //                 let valid = false;
    //                 parent.parameters.
    //                 value?.parameters.map()
    //
    //                 return valid ? true : createError({message: "A value must be specified"});
    //             }
    //         }),
    // });

    const basicSubmitButtonRef = useRef<HTMLButtonElement>(null);
    const basicClearButtonRef = useRef<HTMLButtonElement>(null);
    const advancedSubmitButtonRef= useRef<HTMLButtonElement>(null);
    const fileSubmitButtonRef= useRef<HTMLButtonElement>(null);

    const BasicSearchFormikForm = useMemo(() => withFormik<BasicSearchFormProps, BasicSearchFormParams>({

        validateOnBlur: false,
        validateOnChange: false,

        validationSchema: basicSearchValidationSchema,

        mapPropsToValues: (props) => {
            const { addDate, destroyDate, statusDate, contentsDate, ...rest } = props.searchValues;

            return {
                ...rest,
                addDate: [parseDate(addDate.from), parseDate(addDate.to)] as DateRange<Date>,
                destroyDate: [parseDate(destroyDate.from), parseDate(destroyDate.to)] as DateRange<Date>,
                statusDate: [parseDate(statusDate.from), parseDate(statusDate.to)] as DateRange<Date>,
                contentsDate: [parseDate(contentsDate.from), parseDate(contentsDate.to)] as DateRange<Date>,
            };
        },

        handleSubmit: handleBasicSubmit,
    })(BasicSearchForm), [basicSearch.params]);

    const AdvancedSearchFormikForm = useMemo(() => withFormik<AdvancedSearchFormProps, AdvancedSearchFormParams>({

        validateOnBlur: false,
        validateOnChange: false,

        // validationSchema: advancedSearchValidationSchema,

        mapPropsToValues: (props) => {
            const { code, parameters } = props.searchValues;

            let initialOption = props.advancedSearches.find((item: AdvancedSearch) => item.code === code);
            if (initialOption === undefined) {
                initialOption = props.advancedSearches[0];
            }

            const convertedParams:AdvancedSearchFormParamType = {};
            parameters.forEach((item) => convertedParams[item.code] = item.value);

            return {
                selectedOption: initialOption,
                parameters: convertedParams,
            }
        },

        handleSubmit: handleAdvancedSubmit,
    })(AdvancedSearchForm), [advancedSearch.params]);

    const handleFileSubmit = async (values: FileSearchFormParams, formikBag: FormikBag<FileSearchFormProps, FileSearchFormParams>) => {
        if (values.selectedFile === undefined) {
            console.error("Can't proceed with empty file");
            return;
        }

        const formData = new FormData();
        formData.append("gdgFile", values.selectedFile);

        if (values.fieldType === FileSearchFieldType.Barcode || values.fieldType === FileSearchFieldType.AltCode) {
            formData.append(
                "gdgJson",
                JSON.stringify({
                    type: values.itemType,
                    field: values.fieldType,
                })
            );
        } else {
            formData.append(
                "gdgJson",
                JSON.stringify({
                    type: values.itemType,
                    field: values.fieldType,
                    delimiter: values.delimiterType !== FileSearchDelimiterType.Other
                        ? values.delimiterType
                        : values.delimiterChar,
                })
            );
        }

        try {
            const data = await getItemsFileSearch(formData).unwrap();
            dispatch(setFileSearchParams({file: values.selectedFile.name}));
            const dateStr = format(new Date(), "dd/MM/yyyy HH:mm:ss");
            dispatch(setInventoryDataRetrievedTS(`${dateStr} using file ${values.selectedFile.name}`));
            dispatch(setInventoryData(data));
            history.replace("/inventory");
        } catch (error) {
            const err = error as FetchBaseQueryError;
            console.error("Failed to submit search file");
            console.error(err);

            alertDispatch({
                type: 'add',
                payload: {
                    type: "general",
                    message: "Failed to submit search file",
                }
            });
        }
    };

    const FileSearchFormikForm = useMemo(() => withFormik<FileSearchFormProps, FileSearchFormParams>({

        validateOnBlur: false,
        validateOnChange: false,

        validationSchema: Yup.object().shape({
            selectedFile: Yup.mixed()
                .test({
                    name: "delimiterChar",
                    test: async (value, { createError }) => {
                        if (value === undefined) {
                            return createError({message: "You must select a file"});
                        }
                        if (value.size === 0) {
                            return createError({message: "You cannot select an empty file"});
                        }
                        return true;
                    }
                }),

            delimiterChar: Yup.mixed()
                .test({
                    name: "delimiterChar",
                    test: async (value, { parent, createError }) => {
                        if (parent.delimiterChar !== FileSearchDelimiterType.Other) {
                            return true;
                        }

                        if (value.length != 0) {
                            return createError({message: "You must enter a delimiter"});
                        }

                        return true;
                    }
                }),
        }),

        mapPropsToValues: () => {
            return {
                itemType: FileSearchItemType.Item,
                fieldType: FileSearchFieldType.Barcode,
                delimiterType: FileSearchDelimiterType.Comma,
                delimiterChar: "",
            };
        },

        handleSubmit: handleFileSubmit,
    })(FileSearchForm), []);

    // Click the hidden "submit" button in the appropriate
    // form to trigger Yup validation and form submission
    const handleSearchClick = () => {
        if (searchType === SearchTab.basic) {
            basicSubmitButtonRef?.current?.click();
        } else if (searchType === SearchTab.advanced) {
            advancedSubmitButtonRef?.current?.click();
        } else {
            fileSubmitButtonRef?.current?.click();
        }
    }

    return (
        <Box sx={{ height: '90vh' }}>
            <TabContext value={searchType}>
                <GDGAppBar title={'Search inventory'} drawerWidth={0} onClose={handleClose}
                           tabs={
                               <TabList
                                   onChange={(_, newTab) => handleSearchTabChange(newTab)}
                                   TabIndicatorProps={{
                                       style: {
                                           background: "#db291d",
                                       }
                                   }}
                               >
                                   <Tab label="Basic" value={SearchTab.basic} sx={{width: "160px"}}/>
                                   <Tab label="Advanced" value={SearchTab.advanced} sx={{width: "160px"}}/>
                                   <Tab label="Upload file" value={SearchTab.file} sx={{width: "160px"}}/>
                               </TabList>
                           }
                >
                    {
                        searchType === SearchTab.basic &&
                        <GDGAppBarButton
                            onClick={() => basicClearButtonRef?.current?.click()}
                            text='Clear'
                        />
                    }

                    <GDGAppBarButton
                        onClick={handleSearchClick}
                        text='Search'
                        icon={['fas', 'search']}
                    />
                </GDGAppBar>

                <TabPanel value={SearchTab.basic} sx={{marginTop: 5}}>
                    <BasicSearchFormikForm
                        searchValues={basicSearch.params}
                        submitButtonRef={basicSubmitButtonRef}
                        clearButtonRef={basicClearButtonRef}
                    />
                </TabPanel>
                <TabPanel value={SearchTab.advanced} sx={{marginTop: 5}}>
                    <AdvancedSearchFormikForm
                        advancedSearches={userData ? userData.advancedSearches : []}
                        searchValues={advancedSearch.params}
                        submitButtonRef={advancedSubmitButtonRef}
                    />
                </TabPanel>
                <TabPanel value={SearchTab.file} sx={{marginTop: 5}}>
                    <FileSearchFormikForm
                        submitButtonRef={fileSubmitButtonRef}
                    />
                </TabPanel>
            </TabContext>
        </Box>
    );
}
